import React, { useEffect, useMemo, useRef, useState } from "react";

const COPY = {
  en: {
    workspaceLabel: "Contract PDF workspace",
    generated: "View generated contract",
    signed: "View signed contract",
    generatedTitle: "Generated contract PDF",
    signedTitle: "Signed contract PDF",
    contractSerial: "Contract serial",
    attemptNumber: "Upload attempt",
    loading: "Loading the protected contract…",
    missing: "This contract file is not available.",
    generatedError: "The generated contract could not be loaded.",
    signedError: "The signed contract could not be loaded.",
    retryGenerated: "Retry generated contract",
    retrySigned: "Retry signed contract",
    refresh: "Refresh PDF",
    download: "Download PDF",
    fullscreen: "View PDF full screen",
    browserFallback: "Download the authenticated PDF if preview is unavailable."
  },
  ar: {
    workspaceLabel: "مساحة عرض ملفات العقد",
    generated: "عرض العقد المنشأ",
    signed: "عرض العقد المُوقع",
    generatedTitle: "ملف العقد المنشأ PDF",
    signedTitle: "ملف العقد المُوقع PDF",
    contractSerial: "رقم العقد",
    attemptNumber: "محاولة الرفع",
    loading: "جارٍ تحميل ملف العقد المحمي…",
    missing: "ملف العقد غير متاح.",
    generatedError: "تعذر تحميل العقد المنشأ.",
    signedError: "تعذر تحميل العقد المُوقع.",
    retryGenerated: "إعادة محاولة تحميل العقد المنشأ",
    retrySigned: "إعادة محاولة تحميل العقد المُوقع",
    refresh: "تحديث ملف PDF",
    download: "تنزيل ملف PDF",
    fullscreen: "عرض ملف PDF بملء الشاشة",
    browserFallback: "يمكن تنزيل ملف PDF الموثق إذا تعذرت المعاينة."
  }
};

export const useContractPdfBlob = ({
  contractId,
  attemptId,
  type,
  enabled,
  retryNonce,
  api
}) => {
  const [state, setState] = useState({ status: enabled ? "loading" : "idle" });

  useEffect(() => {
    if (!enabled || !contractId || !api?.fetchContractPdf) {
      setState({ status: "idle" });
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl;
    setState({ status: "loading" });
    Promise.resolve(
      api.fetchContractPdf({
        contractId,
        attemptId,
        type,
        signal: controller.signal
      })
    )
      .then((blob) => {
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = undefined;
          return;
        }
        setState({ status: "ready", objectUrl });
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.name !== "AbortError") {
          setState({ status: "error" });
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, attemptId, contractId, enabled, retryNonce, type]);

  return state;
};

const contractIdentity = (operation) => {
  const contract = operation?.contract || {};
  const signedAttempt =
    contract.latestSignedUploadAttempt || operation?.signedUpload || null;
  return {
    contractId:
      contract.contract ||
      contract.id ||
      operation?.generatedContract?.contract ||
      null,
    serial: contract.serial || operation?.generatedContract?.serial || "—",
    generatedAvailable: Boolean(
      contract.generatedFile?.available ||
        operation?.generatedContract?.hasGeneratedFile
    ),
    signedAttempt,
    signedAvailable: Boolean(signedAttempt?.id)
  };
};

const ContractPdfPane = ({ type, state, active, copy, onRetry, paneRef }) => {
  const title = type === "generated" ? copy.generatedTitle : copy.signedTitle;
  const retryLabel =
    type === "generated" ? copy.retryGenerated : copy.retrySigned;
  const error = type === "generated" ? copy.generatedError : copy.signedError;

  return (
    <article
      className={`contract-pdf-pane is-${state.status}`}
      aria-hidden={!active}
      data-contract-type={type}
    >
      {state.status === "loading" && (
        <div className="contract-pdf-skeleton" aria-label={copy.loading}>
          <span />
          <span />
          <span />
        </div>
      )}
      {state.status === "ready" && (
        <>
          <iframe
            ref={paneRef}
            src={state.objectUrl}
            title={title}
            loading="lazy"
          />
          <p className="contract-pdf-fallback">{copy.browserFallback}</p>
        </>
      )}
      {state.status === "error" && (
        <div className="contract-pdf-state" role="alert">
          <i className="fa-solid fa-file-circle-exclamation" aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="btn btn-outline-primary" onClick={onRetry}>
            {retryLabel}
          </button>
        </div>
      )}
      {state.status === "idle" && (
        <div className="contract-pdf-state">
          <i className="fa-solid fa-file-circle-xmark" aria-hidden="true" />
          <p>{copy.missing}</p>
        </div>
      )}
    </article>
  );
};

const ContractPdfWorkspace = ({
  operation,
  api,
  locale = "en",
  onSignedViewed
}) => {
  const copy = COPY[locale] || COPY.en;
  const identity = useMemo(() => contractIdentity(operation), [operation]);
  const initialType = identity.generatedAvailable ? "generated" : "signed";
  const [activeType, setActiveType] = useState(initialType);
  const [retry, setRetry] = useState({ generated: 0, signed: 0 });
  const paneRefs = useRef({ generated: null, signed: null });

  useEffect(() => {
    setActiveType(identity.generatedAvailable ? "generated" : "signed");
    setRetry({ generated: 0, signed: 0 });
  }, [identity.contractId, identity.signedAttempt?.id, identity.generatedAvailable]);

  const generatedState = useContractPdfBlob({
    contractId: identity.contractId,
    type: "generated",
    enabled: identity.generatedAvailable,
    retryNonce: retry.generated,
    api
  });
  const signedState = useContractPdfBlob({
    contractId: identity.contractId,
    attemptId: identity.signedAttempt?.id,
    type: "signed",
    enabled: identity.signedAvailable,
    retryNonce: retry.signed,
    api
  });
  const states = { generated: generatedState, signed: signedState };

  useEffect(() => {
    if (
      activeType === "signed" &&
      signedState.status === "ready" &&
      identity.signedAttempt?.id
    ) {
      onSignedViewed?.(identity.signedAttempt.id);
    }
  }, [
    activeType,
    identity.signedAttempt?.id,
    onSignedViewed,
    signedState.status
  ]);

  const refresh = (type) =>
    setRetry((current) => ({ ...current, [type]: current[type] + 1 }));

  const download = () => {
    const objectUrl = states[activeType].objectUrl;
    if (!objectUrl) return;
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `${identity.serial}-${activeType}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const fullscreen = () => {
    const pane = paneRefs.current[activeType];
    if (pane?.requestFullscreen) pane.requestFullscreen();
  };

  return (
    <section className="contract-pdf-workspace" aria-label={copy.workspaceLabel}>
      <div className="contract-pdf-meta">
        <span>{copy.contractSerial}</span>
        <strong>{identity.serial}</strong>
        {identity.signedAttempt?.attemptNumber && (
          <small>
            {copy.attemptNumber}: {identity.signedAttempt.attemptNumber}
          </small>
        )}
      </div>
      <div className="contract-pdf-toolbar">
        <div className="contract-pdf-selectors" role="group" aria-label={copy.workspaceLabel}>
          <button
            type="button"
            className={activeType === "generated" ? "is-active" : ""}
            disabled={!identity.generatedAvailable}
            aria-pressed={activeType === "generated"}
            onClick={() => setActiveType("generated")}
          >
            <i className="fa-regular fa-file-lines" aria-hidden="true" />
            {copy.generated}
          </button>
          <button
            type="button"
            className={activeType === "signed" ? "is-active" : ""}
            disabled={!identity.signedAvailable}
            aria-pressed={activeType === "signed"}
            onClick={() => setActiveType("signed")}
          >
            <i className="fa-solid fa-file-signature" aria-hidden="true" />
            {copy.signed}
          </button>
        </div>
        <div className="contract-pdf-actions">
          <button
            type="button"
            aria-label={copy.refresh}
            title={copy.refresh}
            disabled={states[activeType].status === "idle"}
            onClick={() => refresh(activeType)}
          >
            <i className="fa-solid fa-rotate" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={copy.download}
            title={copy.download}
            disabled={states[activeType].status !== "ready"}
            onClick={download}
          >
            <i className="fa-solid fa-download" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={copy.fullscreen}
            title={copy.fullscreen}
            disabled={states[activeType].status !== "ready"}
            onClick={fullscreen}
          >
            <i className="fa-solid fa-expand" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="contract-pdf-panes">
        <ContractPdfPane
          type="generated"
          state={generatedState}
          active={activeType === "generated"}
          copy={copy}
          onRetry={() => refresh("generated")}
          paneRef={(node) => {
            paneRefs.current.generated = node;
          }}
        />
        <ContractPdfPane
          type="signed"
          state={signedState}
          active={activeType === "signed"}
          copy={copy}
          onRetry={() => refresh("signed")}
          paneRef={(node) => {
            paneRefs.current.signed = node;
          }}
        />
      </div>
    </section>
  );
};

export default ContractPdfWorkspace;
